import React from "react";
import {
    Paper,
    Grid,
    ListItem,
    IconButton,
    ListItemAvatar,
    ListItemText,
    Stack,
    Divider,
    Chip,
    Box,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckIcon from "@mui/icons-material/Check";
import moment from "moment";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import SpeakerNotesTwoToneIcon from "@mui/icons-material/SpeakerNotesTwoTone";

function DeleteExpenseModal(props) {
    const { deleteMile, expense, openDelete, setOpenDelete } = props;

    const handleClose = () => {
        setOpenDelete(false);
    };

    const handleDelete = () => {
        deleteMile(expense.id);
        setOpenDelete(false);
    };

    return (
        <div>
            <Dialog
                open={openDelete}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Alert severity="error">
                        Permanently delete these miles?
                    </Alert>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Date:{" "}
                        {moment(expense.date_purchased).format(
                            "ddd, MMMM Do YYYY"
                        )}
                        <br />
                        {expense.quote ? `Quote: ${expense.quote.number}` : ""}
                        {expense.project
                            ? `Project: ${expense.project.number}`
                            : ""}
                        {expense.service
                            ? `Service: ${expense.service.number}`
                            : ""}
                        {expense.hse ? `HSE: ${expense.hse.number}` : ""}
                        <br />
                        Miles: {expense.miles}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default function MileCard(props) {
    const {
        darkState,
        user,
        expense,
        approveMile,
        deleteMile,
        setOpenMiles,
        setEditExpense,
        setEditing,
        isAdmin,
    } = props;
    const [openDelete, setOpenDelete] = React.useState(false);
    const [openProcessedInfo, setOpenProcessedInfo] = React.useState(false);
    const cardIsEditable = !expense.is_approved;

    const handleEdit = () => {
        if (!cardIsEditable) {
            return;
        }
        setEditExpense(expense);
        setEditing(true);
        setOpenMiles(true);
    };

    const handleDelete = (event) => {
        if (event && typeof event.stopPropagation === "function") {
            event.stopPropagation();
        }
        setOpenDelete(true);
    };

    const handleShowProcessedInfo = () => {
        setOpenProcessedInfo(true);
    };

    const handleCloseProcessedInfo = () => {
        setOpenProcessedInfo(false);
    };

    const handleCardClick = () => {
        if (cardIsEditable) {
            handleEdit();
        } else {
            handleShowProcessedInfo();
        }
    };

    const handleCardKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (cardIsEditable) {
                handleEdit();
            } else {
                handleShowProcessedInfo();
            }
        }
    };

    const handleClickOpenDelete = () => {
        setOpenDelete(true);
    };

    const handleClickOpenEdit = (expense) => {
        setEditExpense(expense);
        setEditing(true);
        setOpenMiles(true);
    };

    return (
        <>
            <Paper
                variant="outlined"
                sx={{
                    my: 2,
                    width: "100%",
                    maxWidth: "500px",
                    border: darkState ? "1.5px solid" : "3px solid",
                    borderColor: "primary.main",
                    borderRadius: "16px",
                    position: "relative",
                    cursor: "pointer",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                        transform: "translateY(-4px)",
                    },
                }}
                onClick={handleCardClick}
                onKeyDown={handleCardKeyDown}
                role="button"
                tabIndex={0}
                key={expense.id}
            >
                {!expense.is_approved ? (
                    <>
                        <IconButton
                            aria-label="delete expense"
                            onClick={handleDelete}
                            size="medium"
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 2,
                            }}
                        >
                            <CloseIcon fontSize="medium" />
                        </IconButton>
                    </>
                ) : null}
                <Grid container wrap="nowrap" spacing={2}>
                    <Grid item xs zeroMinWidth>
                        <ListItem
                            sx={{
                                alignItems: "stretch",
                            }}
                        >
                            <ListItemAvatar
                                sx={{
                                    marginRight: "1rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                <Chip
                                    sx={{
                                        marginTop: "1.5rem",
                                        marginBottom: "1.5rem",
                                    }}
                                    color={`${
                                        expense.is_approved
                                            ? "success"
                                            : "primary"
                                    }`}
                                    icon={
                                        expense.is_approved ? (
                                            <CheckIcon />
                                        ) : (
                                            <DirectionsCarFilledOutlinedIcon />
                                        )
                                    }
                                    label={`$${parseFloat(
                                        expense.price
                                    ).toFixed(2)}`}
                                />

                                <Divider />
                                <ListItemText
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginTop: "1rem",
                                    }}
                                    primary={
                                        user.is_staff || isAdmin ? (
                                            expense.is_approved ? (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<CheckBoxIcon />}
                                                    color="success"
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        approveMile(expense.id);
                                                    }}
                                                >
                                                    Paid
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    color="inherit"
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        approveMile(expense.id);
                                                    }}
                                                >
                                                    Pay
                                                </Button>
                                            )
                                        ) : (
                                            <span>
                                                {expense.is_approved
                                                    ? "Paid"
                                                    : "Pending"}
                                            </span>
                                        )
                                    }
                                />
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <div
                                        style={{
                                            fontWeight: "700",
                                            marginBottom: ".25rem",
                                        }}
                                    >
                                        {moment(expense.date_purchased).format(
                                            "ddd, MMMM Do YYYY"
                                        )}
                                    </div>
                                }
                                secondary={
                                    <>
                                        {/* {user.is_staff? <Chip sx={{mb:1}}label={`${expense.user.first_name} ${expense.user.last_name}`} />:''} */}
                                        {user.is_staff
                                            ? `${expense.user.first_name} ${expense.user.last_name}`
                                            : ""}
                                        {user.is_staff ? <br /> : ""}
                                        {expense.quote
                                            ? `Quote: ${expense.quote.number}`
                                            : ""}
                                        {expense.project
                                            ? `Project: ${expense.project.number}`
                                            : ""}
                                        {expense.service
                                            ? `Service: ${expense.service.number}`
                                            : ""}
                                        {expense.hse
                                            ? `HSE: ${expense.hse.number}`
                                            : ""}
                                        <br />
                                        Mile Rate:{" "}
                                        {`$${parseFloat(
                                            expense.rate.rate
                                        ).toFixed(2)}`}
                                        <br />
                                        Miles: {expense.miles}
                                    </>
                                }
                            />
                        </ListItem>
                    </Grid>
                </Grid>
                {expense.notes ? (
                    <Tooltip title={expense.notes} enterTouchDelay={0}>
                        <IconButton
                            size="small"
                            aria-label="notes"
                            sx={{
                                position: "absolute",
                                right: 12,
                                bottom: 12,
                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <SpeakerNotesTwoToneIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Paper>
            <DeleteExpenseModal
                expense={expense}
                openDelete={openDelete}
                setOpenDelete={setOpenDelete}
                deleteMile={deleteMile}
            />
            <Dialog
                open={openProcessedInfo}
                onClose={handleCloseProcessedInfo}
                aria-labelledby="processed-dialog-title"
            >
                <DialogTitle id="processed-dialog-title">
                    Already Processed
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This mileage expense has already been processed. Contact
                        your administrator if you need to request any changes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseProcessedInfo} autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
